/**
 * Mobile-first fixed bottom navigation (Phase A refresh).
 *
 * 5-slot layout with a raised central Dial FAB:
 *
 *   [ Home ]  [ Messages ]  [ ⬤ Dial ]  [ Wallet ]  [ Profile ]
 *
 * The Dial FAB sits half-overlapping the bar (negative top margin + ring) so
 * the eye is drawn to it — the WhatsApp / Revolut / banking-app pattern.
 *
 * Contacts is intentionally moved out of the bar (it lives in the dialer's
 * Quick Actions row + still has the `/app/contacts` route).
 */
import { NavLink, Link, useLocation } from "react-router-dom";
import { Home, MessageSquare, Wallet, User, Phone } from "lucide-react";

const SIDE_TABS_LEFT = [
  { to: "/app",          label: "Home",     icon: Home,          testId: "bottom-nav-home",     end: true },
  { to: "/app/messages", label: "Messages", icon: MessageSquare, testId: "bottom-nav-messages" },
];
const SIDE_TABS_RIGHT = [
  { to: "/app/wallet",   label: "Wallet",   icon: Wallet,        testId: "bottom-nav-wallet" },
  { to: "/app/profile",  label: "Profile",  icon: User,          testId: "bottom-nav-profile" },
];

export default function CustomerBottomNav() {
  const loc = useLocation();
  const dialActive = loc.pathname.startsWith("/app/dial") || loc.pathname.startsWith("/app/calls");

  return (
    <nav
      data-testid="customer-bottom-nav"
      aria-label="Primary"
      className="md:hidden fixed bottom-0 inset-x-0 z-[10000] bg-white border-t border-brand-line shadow-[0_-2px_18px_rgba(7,27,77,0.06)]"
      style={{ paddingBottom: "max(env(safe-area-inset-bottom), 0px)" }}
    >
      <div className="relative grid grid-cols-5 items-end">
        {SIDE_TABS_LEFT.map((t) => <Tab key={t.to} {...t} />)}

        {/* Central raised Dial FAB */}
        <div className="flex justify-center">
          <Link
            to="/app/dial"
            data-testid="bottom-nav-dial"
            aria-label="Open dialer"
            className={`relative -mt-7 inline-flex h-16 w-16 items-center justify-center rounded-full text-white shadow-[0_14px_28px_-10px_rgba(0,208,90,0.7)] ring-4 ring-white transition-transform active:scale-95 ${
              dialActive
                ? "bg-brand-greenDark scale-[1.02]"
                : "bg-brand-green hover:bg-brand-greenHover"
            }`}
          >
            <Phone className="h-7 w-7 fill-current" strokeWidth={2.2} />
            {dialActive && (
              <span className="absolute -bottom-3 h-1.5 w-1.5 rounded-full bg-brand-green" aria-hidden />
            )}
          </Link>
        </div>

        {SIDE_TABS_RIGHT.map((t) => <Tab key={t.to} {...t} />)}
      </div>
    </nav>
  );
}

function Tab({ to, label, icon: Icon, testId, end }) {
  return (
    <NavLink
      to={to}
      data-testid={testId}
      end={end}
      className={({ isActive }) =>
        `flex flex-col items-center justify-center gap-1 py-2.5 text-[10px] font-semibold transition-colors ${
          isActive ? "text-brand-greenDark" : "text-brand-muted hover:text-brand-ink"
        }`
      }
    >
      {({ isActive }) => (
        <>
          <Icon className={`h-5 w-5 ${isActive ? "stroke-[2.4]" : ""}`} />
          <span className="leading-none">{label}</span>
        </>
      )}
    </NavLink>
  );
}
